
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/types/conversation";
import { DBMessage, MessageMetadata, MessageTone } from "@/types/message";
import { generateAIResponse, addMessage } from "@/utils/api";
import { prepareRegenerationMetadata } from "./regeneration/use-regenerate-metadata";
import { prepareConversationContext } from "./regeneration/use-context-preparation";
import { getNextVersionNumber } from "./regeneration/use-version-tracking";
import { useToastNotifications } from "./regeneration/use-toast-notifications";

export const useRegenerateAIResponse = (id: string | undefined) => {
  const { toast } = useToast();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | undefined>(undefined);
  const toastNotifications = useToastNotifications(toast);

  const handleRegenerateAIResponse = async (
    conversation: Conversation | null,
    messages: DBMessage[],
    messageId: string,
    tone: MessageTone,
    customInstructions?: string
  ) => {
    if (!conversation || !id) {
      console.error("Cannot regenerate: missing conversation or ID");
      return null;
    }
    
    setIsRegenerating(true);
    setRegeneratingMessageId(messageId);
    toastNotifications.showRegenerationStarted(tone, customInstructions);
    
    try {
      console.log('Starting regeneration for message:', messageId);
      
      // Prepare context for regeneration
      const context = prepareConversationContext(conversation, messages, messageId);
      if (!context) {
        throw new Error("Invalid message to regenerate");
      }
      
      const {
        messageToRegenerate,
        previousMessages,
        isInitialResponse,
        sellerResponse,
        messageIndex
      } = context;
      
      // Determine original message ID for version tracking
      const originalMessageId = messageToRegenerate.related_to_message_id || messageId;
      
      // Get next version number
      const nextVersion = await getNextVersionNumber(id, messageId, originalMessageId);
      
      // Use the appropriate message type based on context
      const messageType = isInitialResponse ? "initial" : "seller_response";
      
      console.log('Regeneration context:', {
        messageType,
        originalMessageId,
        nextVersion,
        tone,
        customInstructions
      });
      
      // Include the refund request details for better context
      const fullConversationContext = {
        ...conversation,
        is_regenerating: true
      };
      
      // Generate new AI response with the specified tone and conversation context
      const data = await generateAIResponse(
        messageType, 
        fullConversationContext, 
        previousMessages,
        tone,
        customInstructions,
        conversation.is_demo || false
      );
      
      if (!data?.success || !data?.response) {
        console.error("Failed to generate AI response:", data);
        throw new Error("AI could not generate a valid response. Please try again.");
      }
      
      console.log(`AI response generated successfully: ${data.response.substring(0, 50)}...`);
      
      // Extract metadata from AI response
      const metadata = prepareRegenerationMetadata(data, originalMessageId, customInstructions);
      
      // Create a new message as a regenerated version of the original
      console.log(`Creating new message as regenerated version of ${originalMessageId}`);
      
      const newMessage = await addMessage(
        id,
        "ai",
        data.response,
        tone,
        metadata,
        originalMessageId,
        nextVersion
      );
      
      console.log(`New regenerated message created: ${newMessage.id}`);
      toastNotifications.showRegenerationSuccess(tone, customInstructions);
      
      return newMessage;
    } catch (error: any) {
      console.error("Error in message regeneration process:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        code: error.code,
        conversationId: conversation?.id,
        messageId
      });
      
      // Show more specific error message
      const errorMessage = error.message || "An unexpected error occurred during regeneration";
      toastNotifications.showRegenerationError(new Error(errorMessage));
      return null;
    } finally {
      setIsRegenerating(false);
      setRegeneratingMessageId(undefined);
    }
  };

  return {
    handleRegenerateAIResponse,
    isRegenerating,
    regeneratingMessageId
  };
};
